import { Component, Input, ChangeDetectorRef, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as moment from 'moment';
import { UserService } from '../../../../shared-services/src/lib/users/users.service';

@Component({
    selector: 'app-comments-tooltip',
    imports: [CommonModule],
    template: `
    <div *ngIf="isVisible" 
         class="fixed z-[1000]"
         [style.left]="position.x + 'px'"
         [style.top]="position.y + 'px'"
         (mouseenter)="mouseEnter.emit($event)"
         (mouseleave)="mouseLeave.emit($event)">
      
      <!-- Main Tooltip Content -->
      <div class="bg-white rounded-lg shadow-2xl border border-gray-200 w-96 flex flex-col relative">
        <!-- Header -->
        <div class="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-none">
          <h3 class="text-sm font-semibold text-gray-900">Comment History</h3>
          <p *ngIf="requestName" class="text-xs text-gray-600 mt-1">
            Request: {{ requestName }}
          </p>
        </div>
        
        <!-- Comments Section -->
        <div class="overflow-y-auto max-h-[188px] p-3 space-y-3 custom-scrollbar">
          <div *ngIf="!comments || comments.length === 0" 
               class="flex items-center justify-center h-24 text-gray-500 text-sm">
            No comments yet
          </div>
          
          <div *ngFor="let comment of comments" 
               class="flex space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-150">
            <!-- User Avatar -->
            <div class="flex-shrink-0">
              <div class="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <span class="text-sm font-medium text-indigo-600">
                  {{ getInitials(getUserName(comment)) }}
                </span>
              </div>
            </div>
            
            <!-- Comment Content -->
            <div class="flex-1 min-w-0">
              <div class="flex items-baseline justify-between">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ getUserName(comment) }}
                </p>
                <span class="text-xs text-gray-500 whitespace-nowrap ml-2">
                  {{ formatDate(comment.timestamp) }}
                </span>
              </div>
              <p class="text-sm text-gray-600 mt-1 break-words">
                {{ comment.content }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div *ngIf="comments && comments.length > 0" 
             class="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 text-center rounded-b-lg flex-none">
          {{ comments.length }} comment{{ comments.length === 1 ? '' : 's' }}
        </div>

        <!-- Arrow -->
        <div class="absolute -right-2 top-1/2 -translate-y-1/2 w-0 h-0 border-8 border-transparent border-l-white filter drop-shadow-md"
             style="filter: drop-shadow(2px 0 2px rgba(0,0,0,0.1));">
        </div>
      </div>
    </div>
  `,
    styles: [`
    :host {
      position: fixed;
      z-index: 1000;
    }
    
    .custom-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
    }

    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }

    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: rgba(156, 163, 175, 0.5);
      border-radius: 3px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background-color: rgba(156, 163, 175, 0.7);
    }

    .drop-shadow-md {
      filter: drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06));
    }
  `]
})
export class CommentsTooltipComponent implements OnInit {
  @Input() comments: any[] = [];
  @Input() isVisible: boolean = false;
  @Input() position = { x: 0, y: 0 };
  @Input() requestName: string | undefined = '';
  @Output() mouseEnter = new EventEmitter<MouseEvent>();
  @Output() mouseLeave = new EventEmitter<MouseEvent>();

  private userCache: { [key: string]: any } = {};

  constructor(
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Pre-fetch user data for all comments
    this.comments.forEach(comment => {
      if (typeof comment.user === 'string') {
        this.fetchUserData(comment.user);
      }
    });
  }

  getUserName(comment: any): string {
    // If comment.user is a string (ID), try to get from cache
    if (typeof comment.user === 'string') {
      const cachedUser = this.userCache[comment.user];
      if (cachedUser) {
        return cachedUser.name || 'Unknown User';
      }
      this.fetchUserData(comment.user);
      return 'Loading...';
    }
    
    // Fallback for existing logic
    if (comment.user?.name) {
      return comment.user.name;
    }
    return 'Unknown User';
  }

  private fetchUserData(userId: string) {
    if (!this.userCache[userId]) {
      this.userService.getUserById(userId).subscribe(userData => {
        this.userCache[userId] = userData;
        this.cdr.detectChanges();
      });
    }
  }

  formatDate(date: string | Date): string {
    if (!date) return '';
    return moment(date).format('MMM D, h:mm A');
  }

  getInitials(name: string): string {
    if (!name || name === 'Loading...') return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }
} 